import type { CefrLevel, ExamSection } from "@rmnlng/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./api";
import { progressKeys } from "./progress";

// Mirrored from `auth.ts` — that module uses a private `ME_KEY` const.
// Inlining here avoids exporting a sibling file's internals.
const ME_KEY = ["auth", "me"] as const;

// Question shapes — mirror the server's stripped-of-keys envelopes. The
// type unions stay narrow so React renders can `switch (q.section)` and
// the compiler enforces field access.
export type ExamMcqQuestionView = {
  // Vocab: lemma + direction. Grammar: sentence + hints. Discriminator
  // keeps the renderer simple — `case "vocab_mcq" / "grammar_mcq"`.
  options: string[];
};

export type ExamVocabMcqQuestion = ExamMcqQuestionView & {
  wordId: string;
  lemma: string;
  language: "RO";
  direction: "RO_TO_RU";
};

// Inverted from MCQ: prompt = RU, expected answer = RO. Tests
// production ("can you write the Romanian for this Russian word?"),
// not recognition. The lemma is the RU prompt.
export type ExamVocabTypeQuestion = {
  wordId: string;
  lemma: string;
  language: "RU";
  direction: "RU_TO_RO";
};

export type ExamGrammarMcqQuestion = ExamMcqQuestionView & {
  quizItemId: string;
  sentence: string;
  translation: string | null;
  hints: string[];
};

export type ExamGrammarFillQuestion = {
  quizItemId: string;
  sentence: string;
  translation: string | null;
  hints: string[];
};

export type ExamGrammarTransformQuestion = {
  quizItemId: string;
  sourceSentence: string;
  sourceTranslation: string;
  sentence: string;
  translation: string | null;
  hints: string[];
};

export type ExamQuestionEnvelope =
  | { position: number; section: "vocab_mcq"; question: ExamVocabMcqQuestion }
  | { position: number; section: "vocab_type"; question: ExamVocabTypeQuestion }
  | { position: number; section: "grammar_mcq"; question: ExamGrammarMcqQuestion }
  | { position: number; section: "grammar_fill"; question: ExamGrammarFillQuestion }
  | { position: number; section: "grammar_transform"; question: ExamGrammarTransformQuestion };

export type ExamStartResponse = {
  attemptId: string;
  startedAt: string;
  questions: ExamQuestionEnvelope[];
};

export type ExamSubmitInput = {
  attemptId: string;
  answers: Array<{ position: number; userAnswer: string; flagged: boolean }>;
};

export type ExamSectionScore = {
  section: ExamSection;
  total: number;
  correct: number;
  pct: number;
};

export type ExamSubmitResponse = {
  attempt: {
    id: string;
    fromLevel: CefrLevel;
    toLevel: CefrLevel;
    score: number;
    passed: boolean;
    durationSeconds: number;
    submittedAt: string;
    sections: ExamSectionScore[];
  };
  currentLevel: CefrLevel;
};

/**
 * Start an exam attempt. Server-side validates readyForExam + cooldown
 * + content pool size; the client just calls and renders the response
 * or the failure (403 / 429 / 400). The mutation owns the attempt lifecycle:
 * a fresh `useExamStart` per page mount means navigating away mid-exam
 * abandons the attempt — server keeps it as `submittedAt: null` until
 * cooldown expires (which is fine; rerunning start fails for the same
 * 24h until they actually submit or the row times out).
 */
export function useExamStart(targetLevel: CefrLevel) {
  return useMutation({
    mutationFn: () =>
      apiFetch<ExamStartResponse>(`/levels/${targetLevel}/exam/start`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
  });
}

/**
 * Submit answers + grade in one call. On pass, server has already
 * advanced `User.currentLevel`, so we invalidate `me` (drives the
 * Home + Settings reads) and `progress/levels` (drives the Levels page
 * rings). The Results screen renders from the response itself, not
 * a re-fetch, so no race between submit and screen render.
 */
export function useExamSubmit(targetLevel: CefrLevel) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ExamSubmitInput) =>
      apiFetch<ExamSubmitResponse>(`/levels/${targetLevel}/exam/submit`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => {
      // Both reads need to refresh on pass. On fail nothing user-facing
      // changed, but invalidating is cheap and keeps the code uniform.
      void qc.invalidateQueries({ queryKey: ME_KEY });
      void qc.invalidateQueries({ queryKey: progressKeys.levels });
      // Eagerly hint the next page render with the new level.
      qc.setQueryData(ME_KEY, (prev: { currentLevel?: CefrLevel } | undefined) =>
        prev ? { ...prev, currentLevel: data.currentLevel } : prev,
      );
    },
  });
}

// ─── Review answers ─────────────────────────────────────────────────
//
// Per-row payload includes the FULL question JSON (with answer keys)
// so the Review screen can render "what was correct" alongside "what
// you typed". Server only exposes this post-submission — pre-submit
// attempts return 400. Question is `unknown` because the discriminator
// lives in the section field; the renderer narrows it case-by-case.
export type ExamAttemptDetailAnswer = {
  id: string;
  position: number;
  section: ExamSection;
  question: unknown;
  userAnswer: string;
  correct: boolean;
  flagged: boolean;
};

export type ExamAttemptDetail = {
  attempt: {
    id: string;
    fromLevel: CefrLevel;
    toLevel: CefrLevel;
    score: number | null;
    passed: boolean | null;
    durationSeconds: number | null;
    startedAt: string;
    submittedAt: string;
  };
  answers: ExamAttemptDetailAnswer[];
};

/**
 * Fetch a submitted attempt's full breakdown. `staleTime: Infinity`
 * because submitted attempts are immutable — no point ever refetching.
 */
export function useExamAttempt(targetLevel: CefrLevel, attemptId: string | undefined) {
  return useQuery({
    queryKey: ["exam", "attempt", attemptId ?? ""],
    queryFn: () => apiFetch<ExamAttemptDetail>(`/levels/${targetLevel}/exam/attempt/${attemptId}`),
    enabled: !!attemptId,
    staleTime: Infinity,
  });
}
